import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
  toJS,
} from "mobx";
import ProductToast from "common/Notifications/ProductToast/ProductToast";
import {
  runVendorCounterPromises,
  runYandexVendorOnUtm,
  yandexMetrica,
  yandexMetricaQuantityCartEvent,
} from "services/YandexMetricaApiService/helpers";
import { IStoreElement } from "types/store";
import { ExtendedNextPageContext } from "types/app";
import { sendAddCart, setCartData } from "helpers/mindbox";
import { gtm } from "helpers/analytics";

export type AnalyticsMeta = Record<string, string | number | boolean>;

export default class Cart implements IStoreElement {
  private ctx?: ExtendedNextPageContext;
  @observable groups: Array<Entity.Cart.Groups> = [];

  constructor() {
    makeObservable(this);
  }

  @computed
  get flattenGroups(): Array<Entity.Cart.Item> {
    return this.groups.map(group => toJS(group.items)).flat();
  }

  get count(): number {
    return this.groups.reduce((acc, group) => {
      return acc + group.items.length;
    }, 0);
  }

  getScores(price: number): number {
    return Math.floor(price / 100);
  }

  get priceTotal(): number {
    return this.flattenGroups.reduce((sum, item) => {
      if (
        item.product?.availableForOrder ||
        item.product?.availableForBooking
      ) {
        return sum + item.totalAmount;
      }
      return sum;
    }, 0);
  }

  // get total bonuses without 7th price list
  get totalBonuses(): number {
    const totalPrice = this.flattenGroups.reduce((sum, item) => {
      if (item.product.hasOnlyBookingPrices) {
        return sum;
      }

      if (
        item.product?.availableForOrder ||
        item.product?.availableForBooking
      ) {
        return sum + item.totalAmount;
      }

      return sum;
    }, 0);

    return Math.floor(totalPrice / 100);
  }

  get priceOldTotal(): number {
    return this.discountTotal > 0
      ? this.flattenGroups.reduce((sum, item) => {
          if (
            item.product?.availableForOrder ||
            item.product?.availableForBooking
          ) {
            return sum + (item.amount || item.price);
          }
          return sum;
        }, 0)
      : 0;
  }

  get discountTotal(): number {
    return this.flattenGroups.reduce((sum, item) => {
      if (
        item.product?.availableForOrder ||
        item.product?.availableForBooking
      ) {
        return sum + item.totalDiscount;
      }
      return sum;
    }, 0);
  }

  get withDiscountTotal(): number {
    return this.priceTotal - this.discountTotal;
  }

  @action
  hydrate(data: Partial<Cart>): void {
    if (data.groups && Array.isArray(data.groups)) {
      this.groups = data.groups;
    }
  }

  @action
  async fetch(): Promise<void> {
    const { data, errors } = await this.ctx.api.methods.cart.get({
      regionID: this.ctx.store.location.regionID,
    });
    if (data) {
      runInAction(() => {
        this.groups = data.cart.groups;
      });
    }
    if (errors) {
      this.ctx.store.notifications.showAPIErrors(
        errors,
        "Ошибка при получении данных корзины"
      );
    }
  }

  @action
  async add(
    product: Entity.Product,
    price: Partial<Entity.Product.Price>,
    quantity = 1,
    analyticsMeta?: AnalyticsMeta,
    hideToast = false
  ): Promise<void> {
    const result = await this.update(product, quantity, price, true);
    const item = this.get(product.id, price);
    if (result && item) {
      !hideToast &&
        this.ctx.store.notifications.show({
          type: "toast",
          component: ProductToast,
          props: {
            item: toJS(item),
          },
        });

      sendAddCart({
        product,
        quantity,
      });
      if (analyticsMeta) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore todo: fix when analytics helper will typed
        gtm.cartAdd({ product, quantity, price, ...analyticsMeta });
      }
      setCartData(this);
    }
  }

  @action
  async remove(
    product: Entity.Product,
    price: Partial<Entity.Product.Price>
  ): Promise<void> {
    await this.update(product, 0, price, true);
  }

  @action
  async reset(): Promise<void> {
    const { data } = await this.ctx.api.methods.cart.reset({
      regionID: this.ctx.store.location.regionID,
    });

    if (data) {
      runInAction(() => {
        this.groups = data.clearCart.groups;
      });
    }
  }

  @action
  async setItemCount(item: Entity.Cart.Item, quantity: number): Promise<void> {
    await this.update(
      item.product,
      quantity,
      {
        priceTypeID: item.priceTypeID,
      },
      item.isChecked
    );
  }

  @action
  async update(
    product: Entity.Product | Entity.Product.Short,
    quantity: number,
    price: Partial<Entity.Product.Price>,
    isChecked: boolean
  ): Promise<boolean> {
    const itemInCart = price
      ? this.get(product.id, price as Entity.Product.Price)
      : null;

    const { data, errors } = await this.ctx.api.methods.cart.update({
      regionID: this.ctx.store.location.regionID,
      items: [
        {
          productID: product.id,
          priceTypeID: price.priceTypeID,
          quantity: quantity,
          isChecked,
        },
      ],
    });

    if (data) {
      runInAction(() => {
        this.groups = data.updateCart.groups;
      });

      const eventResult = yandexMetricaQuantityCartEvent(
        itemInCart?.quantity,
        quantity
      );

      runVendorCounterPromises(product.counters, item =>
        runYandexVendorOnUtm(
          tid =>
            yandexMetrica.event.cartChangeCount({
              tid,
              ...eventResult,
              product,
            }),
          item,
          1000
        )
      );
      return true;
    }

    if (errors) {
      this.ctx.store.notifications.show({
        type: "toast",
        props: {
          title: "Ошибка при обновлении корзины",
        },
      });
      return false;
    }
  }

  @action
  async batchUpdate(items: Array<Entity.Cart.Item>): Promise<boolean> {
    const itemsInput: Array<Entity.Cart.Item.Input> = items.map(item => ({
      productID: item.product.id,
      priceTypeID: item.priceTypeID,
      quantity: item.quantity,
      isChecked: item.isChecked,
    }));

    const { data, errors } = await this.ctx.api.methods.cart.update({
      regionID: this.ctx.store.location.regionID,
      items: itemsInput,
    });

    if (data) {
      runInAction(() => {
        this.groups = data.updateCart.groups;
      });
      return true;
    }

    if (errors) {
      this.ctx.store.notifications.show({
        type: "toast",
        props: {
          title: "Ошибка при обновлении корзины",
        },
      });
      return false;
    }
  }

  /* todo: refactor */
  @computed
  get cartItems(): Array<Entity.Cart.Groups> {
    return toJS(this.groups);
  }

  get(
    id: Entity.Product["id"],
    price: Partial<Entity.Product.Price>
  ): Entity.Cart.Item {
    if (!price) return undefined;

    return this.flattenGroups.find(
      item => item.product.id === id && item.priceTypeID === price.priceTypeID
    );
  }

  has(id: Entity.Product["id"]): boolean {
    return this.flattenGroups.some(item => item.product.id === id);
  }
}
